import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Language "mode" — /kn, /gu, /bn, /ar open the site persistently in that language,
 * the same way /market and /global are dedicated modes. The choice is stored in
 * sessionStorage.auraLang and re-applied to the Google Translate widget on every
 * route change, so it survives SPA navigation instead of reverting to English
 * (which a one-shot ?lang= deep-link couldn't guarantee).
 */

const PATH_LANGS: Record<string, string> = { kn: "kn", gu: "gu", bn: "bn", ar: "ar" };

// Right-to-left languages. Google Translate swaps the words to Arabic but never
// flips the page layout, so we set the document direction ourselves whenever an
// RTL language is active (and reset to ltr otherwise).
const RTL_LANGS = new Set(["ar", "he", "iw", "fa", "ur"]);

function applyDirection(lang: string | null) {
  const rtl = !!lang && RTL_LANGS.has(lang);
  document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");
  document.body.classList.toggle("aura-rtl", rtl);
}

function applyTranslate(lang: string) {
  let tries = 0;
  const interval = setInterval(() => {
    const select = document.querySelector(
      "#google_translate_element select",
    ) as HTMLSelectElement | null;
    if (select) {
      clearInterval(interval);
      select.value = lang;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (++tries > 40) {
      clearInterval(interval); // widget never mounted (~10s cap)
    }
  }, 250);
  return () => clearInterval(interval);
}

export function useLanguageMode() {
  const location = useLocation();

  useEffect(() => {
    // Entering via /kn | /gu | /bn locks the session into that language.
    const seg = location.pathname.split("/")[1];
    if (PATH_LANGS[seg]) {
      sessionStorage.setItem("auraLang", PATH_LANGS[seg]);
    }

    const lang = sessionStorage.getItem("auraLang");
    if (!lang || lang === "en") {
      document.body.classList.remove("aura-lang-mode");
      applyDirection(null);
      return;
    }

    // Cookie => a hard reload also lands translated.
    const cookie = `googtrans=/en/${lang};path=/`;
    document.cookie = cookie;
    document.cookie = `${cookie};domain=.${window.location.hostname}`;
    document.body.classList.add("aura-lang-mode");
    applyDirection(lang);

    return applyTranslate(lang);
  }, [location.pathname]);
}
